import { Injectable } from '@angular/core';
import { TasksApiService, TeamRefDto, UserSearchResult, UsersTaggingApiService } from '@thehubz/client/data-access-api';
import { EMPTY, Observable, catchError, lastValueFrom, map, shareReplay } from 'rxjs';
import { TaggingOptions } from '../model/tagging-option.model';
import { Editor2UtilService } from './editor2-util.service';

export interface AllPeople {
  id: string;
  value: string;
  name?: string;
  email?: string;
  link?: string;
  searchTerm?: string;
  highlightName?: string;
}

@Injectable()
export class ApiCallsService {
  private cache: Record<string, Observable<UserSearchResult | TeamRefDto[]>> = {};

  constructor(
    private tasksApiService: TasksApiService,
    private editor2UtilService: Editor2UtilService,
    private usersTaggingApiService: UsersTaggingApiService,
  ) {}

  /**
   * list users with same agency client and brands
   * @param searchTerm
   * @param taggingOptions
   * @returns
   */
  async mentionsUsers(searchTerm: string, taggingOptions: TaggingOptions, usersSet: string[]) {
    const acbString =
      taggingOptions.agency && taggingOptions.client && taggingOptions.brand
        ? `${taggingOptions.agency},${taggingOptions.client},${taggingOptions.brand}`
        : undefined;
    const allPeople = await lastValueFrom(this.getMentionsUsers(taggingOptions.id || '', taggingOptions.callFrom || '', taggingOptions.roles, acbString));

    const allPeopleWithName: AllPeople[] = allPeople.result
      .filter((user) => !usersSet.includes(user._id))
      .map((user) => {
        return {
          id: user._id,
          value: user.firstName + ' ' + user.lastName,
          name: user.firstName + ' ' + user.lastName,
          highlightName: user.firstName + ' ' + user.lastName,
          email: user.email,
          link: `user/profile?userId=${user._id}`,
        };
      });

    if (taggingOptions.id && taggingOptions.name) {
      if (taggingOptions.callFrom === 'task') {
        const uniqueWorkflowSteps = await lastValueFrom(this.getTaskTeams(taggingOptions.id || ''));
        // add the teams list related to task
        uniqueWorkflowSteps.forEach((step) => {
          allPeopleWithName.unshift({
            id: step._id,
            value: `Team | ${step.name}`,
            name: 'team',
          });
        });
      }

      // add context (project/task) to the list
      if (!usersSet.includes(taggingOptions.id)) {
        allPeopleWithName.unshift({
          id: taggingOptions.id || '',
          value: `${taggingOptions.callFrom?.toUpperCase()} | ${taggingOptions.name}`,
          name: taggingOptions.callFrom,
        });
      }
    }

    return this.highlightPeople(
      allPeopleWithName.filter((person) => person.value.toLowerCase().includes(searchTerm.toLowerCase())),
      searchTerm,
    );
  }

  /**
   * Highlight searched text
   * @param people
   * @param searchString
   * @returns
   */
  highlightPeople(people: AllPeople[], searchString: string): AllPeople[] {
    if (!searchString) {
      return people;
    }
    const regexStr = new RegExp(searchString, 'gi');
    return people.map((val) => ({ ...val, highlightName: val.name?.replace(regexStr, '<b>$&</b>') }));
  }

  clear() {
    this.cache = {};
  }

  private getTaskTeams(id: string) {
    const cacheKey = 'task$' + id;
    if (!this.cache[cacheKey]) {
      this.cache[cacheKey] = this.tasksApiService.tasksControllerFindOne(id).pipe(
        shareReplay(1),
        map((task) => {
          const workflowAllStep = task.workflow?.allSteps?.filter((step) => step.statefullStepFlag).map((step) => JSON.stringify(step.team)) || [];
          const uniqueWorkflowSteps: TeamRefDto[] = this.editor2UtilService.removeListDuplicate(workflowAllStep);
          return uniqueWorkflowSteps;
        }),
        catchError(() => {
          delete this.cache[cacheKey];
          return EMPTY;
        }),
      );
    }
    return this.cache[cacheKey] as Observable<TeamRefDto[]>;
  }

  private getMentionsUsers(id: string, callFrom: string, roles?: string[], acbString?: string) {
    const rolesString = roles?.join(',');
    const cacheKey = 'mentionsUsers$' + id + '.' + callFrom + '.' + rolesString + '.' + acbString;
    if (!this.cache[cacheKey]) {
      this.cache[cacheKey] = this.usersTaggingApiService.usersTaggingControllerUsersMentions(id, callFrom, rolesString, acbString).pipe(
        shareReplay(1),
        catchError(() => {
          delete this.cache[cacheKey];
          return EMPTY;
        }),
      );
    }
    return this.cache[cacheKey] as Observable<UserSearchResult>;
  }
}
